// Shared, non-destructive color/light grade model + LUT compiler.
//
// The point/color pipeline below is the single source of truth for the "look".
// `compileLut` bakes it into a 3D LUT that the WebGL preview samples AND that
// the backend writes to a .cube for ffmpeg `lut3d` — so preview == export.
//
// IMPORTANT: keep applyGradePixel() byte-for-byte equivalent to the mirror in
// backend/api/grade.js. Spatial ops (texture/clarity/dehaze/vignette/grain/
// sharpness) are NOT in the LUT — they run after it (GLSL / ffmpeg) since they
// depend on neighbouring pixels.

import { gradingConfig } from './gradingDefaults.js';

export const HSL_BANDS = 8;
// HSV hue centers (degrees) for the 8 color-mixer bands.
const BAND_HUE = [0, 30, 60, 120, 180, 240, 280, 320];

export const DEFAULT_GRADE = {
  // Input color science. 'srgb' = footage is already display Rec.709/sRGB.
  // 'log3g10' decodes RED Log3G10 + RED Wide Gamut → Rec.709 (IPP2-style), the
  // fix for the flat/monotone RED look. The others decode common camera logs.
  inputTransform: 'srgb', // srgb | log3g10 | slog3 | clog3 | vlog | hlg | linear
  // Display rendering transform (DRT) applied while decoding a camera log to
  // Rec.709 — the tunable side of the input transform (ignored when
  // inputTransform === 'srgb'). Defaults reproduce the original fixed IPP2-style
  // decode exactly, so existing looks are unchanged.
  //   tonemap : highlight tone-mapping strength (off | low | medium | high)
  //   rolloff : extra highlight roll-off knee   (off | low | medium | high)
  //   output  : display encoding                (rec709 | srgb)
  //   exposure: scene-linear exposure comp in STOPS, applied BEFORE the tone-map
  //             (the only place a lift recovers crushed shadow detail). 0 = off,
  //             so all existing looks are byte-identical. Auto-set per RED clip
  //             to normalize differently-exposed log footage; user-tunable.
  display: { tonemap: 'medium', rolloff: 'off', output: 'rec709', exposure: 0 },
  profile: 'adobe-color', // adobe-color | neutral | flat
  wb: { temp: 0, tint: 0 }, // -100..100
  basic: { exposure: 0, contrast: 0, highlights: 0, shadows: 0, whites: 0, blacks: 0 },
  // skinProtect (0..100) holds orange/skin hues back from vibrance/saturation.
  presence: { texture: 0, clarity: 0, dehaze: 0, vibrance: 0, saturation: 0, skinProtect: 0, denoise: 0 },
  curve: { master: null, red: null, green: null, blue: null }, // null = identity; else [{x,y}]
  // Secondary "color warper" curves (Resolve-style). Each null = identity, else
  // [{x,y}] in 0..1. hueHue/hueSat/hueLum: x = hue (0..1 = 0..360°). satSat/
  // lumSat: x = source saturation / luma. y=0.5 is neutral for shift curves;
  // y is a direct multiplier mapped around 0.5 for the *Sat curves.
  hsCurves: { hueHue: null, hueSat: null, hueLum: null, satSat: null, lumSat: null },
  hsl: Array.from({ length: HSL_BANDS }, () => ({ h: 0, s: 0, l: 0 })),
  colorGrading: {
    shadows: { h: 0, s: 0, l: 0 },
    midtones: { h: 0, s: 0, l: 0 },
    highlights: { h: 0, s: 0, l: 0 },
    global: { h: 0, s: 0, l: 0 },
    blending: 50,
  },
  // halation/glow are spatial highlight bloom (warm halation + soft glow),
  // applied post-LUT in GLSL (preview) and ffmpeg (export).
  effects: { vignette: 0, grain: 0, sharpness: 0, halation: 0, glow: 0 },
  // Local adjustment masks (Lightroom-style). Each is a radial ellipse in the
  // 0..1 space of the *framed* (post-crop) image with a small set of local
  // tweaks blended in by a feathered alpha. Applied post-LUT in GLSL (preview)
  // and an ffmpeg geq (export) using the SAME math so preview == bake. [] = none.
  masks: [],
  // Healing / spot removal. Clone spots applied in SOURCE space (before crop):
  // { id, x, y, r, feather, ox, oy } normalized to the source frame — (x,y) is
  // the blemish center, (ox,oy) the source offset to clone from. Mirrored by the
  // GLSL healedSample() and ffmpeg healFilters() so preview == bake. [] = none.
  heal: [],
};

// Default local adjustment for a new mask (no-op until the user tweaks it).
export const DEFAULT_MASK_ADJ = { exposure: 0, contrast: 0, saturation: 0, temp: 0, tint: 0, black: 0 };

// Precompute the per-mask scalar coefficients shared by the GLSL preview, the
// ffmpeg geq export and the JS reference — keep these three in lock-step.
export function maskScalars(adj) {
  const a = adj || {};
  return {
    ev: Math.pow(2, a.exposure || 0),
    blk: (a.black || 0) / 100,
    k: 1 + (a.contrast || 0) / 100,
    tF: (a.temp || 0) / 100,
    tnF: (a.tint || 0) / 100,
    f: 1 + (a.saturation || 0) / 100,
  };
}

const maskAdjActive = (adj) => {
  const a = adj || {};
  return !!(a.exposure || a.contrast || a.saturation || a.temp || a.tint || a.black);
};

// Default luminance-range gate (a color/luma keyer) for a mask — off by default.
export const DEFAULT_MASK_RANGE = { enabled: false, lo: 0, hi: 1, feather: 0.15 };
// Default hue/chroma gate (a colour keyer) — targets a hue band with a minimum
// chroma so e.g. the teal water can be isolated from the (blue, low-chroma) sky.
// hue/width in DEGREES; chromaMin/feather in chroma magnitude units (0..2, where
// 2r-g-b / sqrt3*(g-b) gives ~2 for a fully saturated primary). Off by default.
export const DEFAULT_MASK_COLOR = { enabled: false, hue: 190, width: 28, chromaMin: 0.12, feather: 0.1 };

// Normalized list of masks that actually do something (visible + non-identity).
// radial ellipses, linear gradients, and full-frame colour-keyed masks ('color').
export function activeMasks(grade) {
  const list = (grade && grade.masks) || [];
  return list.filter(
    (m) =>
      m &&
      (m.type === 'radial' || m.type === 'linear' || m.type === 'color') &&
      (m.amount == null || m.amount > 0) &&
      maskAdjActive(m.adj),
  );
}

export const clone = (g) => JSON.parse(JSON.stringify(g));

// Derive a partial grade for layer-stack previews:
//   'light' = tonal only (exposure/contrast/region + master curve + presence
//             detail), all color neutralized.
//   'color' = light + full color, minus the final "look" effects.
//   'full'  = the complete grade.
export function gradeLayer(grade, layer = 'full') {
  const g = clone(grade || DEFAULT_GRADE);
  if (layer === 'full') return g;
  if (layer === 'color') {
    g.effects = { vignette: 0, grain: 0, sharpness: 0 };
    return g;
  }
  // 'light': strip everything that shifts color.
  g.wb = { temp: 0, tint: 0 };
  g.presence = { ...g.presence, vibrance: 0, saturation: 0, skinProtect: 0 };
  g.curve = { ...g.curve, red: null, green: null, blue: null };
  g.hsCurves = { hueHue: null, hueSat: null, hueLum: null, satSat: null, lumSat: null };
  g.hsl = (g.hsl || []).map(() => ({ h: 0, s: 0, l: 0 }));
  g.colorGrading = {
    shadows: { h: 0, s: 0, l: 0 },
    midtones: { h: 0, s: 0, l: 0 },
    highlights: { h: 0, s: 0, l: 0 },
    global: { h: 0, s: 0, l: 0 },
    blending: g.colorGrading?.blending ?? 50,
  };
  g.effects = { vignette: 0, grain: 0, sharpness: 0 };
  return g;
}

// Compose a grade from INDEPENDENT layer toggles (for the live layer-stack
// preview where each layer can be switched on/off). With every layer off the
// result equals DEFAULT_GRADE (so the right pane matches the untouched left).
//   light   = tonal: profile base, exposure/contrast/region, master curve.
//   color   = white balance, vibrance/saturation, per-channel curves, color
//             mixer (HSL), color-grading wheels.
//   effects = clarity/texture/dehaze + vignette/grain/sharpness.
export function composeGrade(grade, layers = {}) {
  const { light = true, color = true, effects = true } = layers;
  const g = clone(grade || DEFAULT_GRADE);
  if (!light) {
    g.inputTransform = 'srgb';
    g.profile = DEFAULT_GRADE.profile;
    g.basic = { ...DEFAULT_GRADE.basic };
    g.curve = { ...g.curve, master: null };
  }
  if (!color) {
    g.wb = { temp: 0, tint: 0 };
    g.presence = { ...g.presence, vibrance: 0, saturation: 0, skinProtect: 0 };
    g.curve = { ...g.curve, red: null, green: null, blue: null };
    g.hsCurves = { hueHue: null, hueSat: null, hueLum: null, satSat: null, lumSat: null };
    g.hsl = (g.hsl || []).map(() => ({ h: 0, s: 0, l: 0 }));
    g.colorGrading = {
      shadows: { h: 0, s: 0, l: 0 },
      midtones: { h: 0, s: 0, l: 0 },
      highlights: { h: 0, s: 0, l: 0 },
      global: { h: 0, s: 0, l: 0 },
      blending: g.colorGrading?.blending ?? 50,
    };
  }
  if (!effects) {
    g.presence = { ...g.presence, clarity: 0, texture: 0, dehaze: 0 };
    g.effects = { vignette: 0, grain: 0, sharpness: 0, halation: 0, glow: 0 };
  }
  return g;
}

// Blend two grades by `amount` (0..1) — a "preset strength" dial. Numeric leaves
// interpolate linearly; equal-length arrays (HSL bands, curve points) and nested
// objects (color-grading wheels, display, wb/basic…) recurse; non-numeric /
// mismatched leaves (profile, inputTransform, tone-map, null curves) snap to the
// target once amount ≥ 0.5. `_preset` metadata is ignored (re-stamp after). This
// lets a strong look be dialed back per clip while staying byte-stable at 0/1.
const roundSmart = (v, a, b) =>
  (Number.isInteger(a) && Number.isInteger(b) ? Math.round(v) : Math.round(v * 1000) / 1000);
function blendVal(a, b, t) {
  if (typeof a === 'number' && typeof b === 'number') return roundSmart(a + (b - a) * t, a, b);
  if (Array.isArray(a) && Array.isArray(b) && a.length === b.length) {
    return a.map((x, i) => blendVal(x, b[i], t));
  }
  if (a && b && typeof a === 'object' && typeof b === 'object' && !Array.isArray(a) && !Array.isArray(b)) {
    const out = {};
    for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) {
      const av = a[k];
      const bv = b[k];
      out[k] = av === undefined ? clone(bv) : bv === undefined ? clone(av) : blendVal(av, bv, t);
    }
    return out;
  }
  return t >= 0.5 ? clone(b) : clone(a);
}
export function blendGrade(base, target, amount) {
  const t = Math.max(0, Math.min(1, amount));
  if (t <= 0) return clone(base || DEFAULT_GRADE);
  if (t >= 1) return clone(target || DEFAULT_GRADE);
  const a = clone(base || DEFAULT_GRADE);
  const b = clone(target || DEFAULT_GRADE);
  delete a._preset;
  delete b._preset;
  return blendVal(a, b, t);
}

// Normalize + sort a keyframe list ([{ id?, t, grade }]) by time. Drops entries
// without a usable time or grade. Returned array is a shallow copy (safe to read).
export function sortKeyframes(keyframes) {
  return (Array.isArray(keyframes) ? keyframes : [])
    .filter((k) => k && k.grade && Number.isFinite(Number(k.t)))
    .map((k) => ({ ...k, t: Number(k.t) }))
    .sort((a, b) => a.t - b.t);
}

// Resolve the interpolated grade at clip-time `t` (seconds) from a list of
// keyframes [{ t, grade }]. Between two keys, blendGrade linearly tweens the
// point pipeline; OUTSIDE the keyframe span the nearest endpoint is held, so the
// whole clip is always well-defined. Masks/heal are NOT interpolated (their
// arrays can differ in shape between keys) — they're carried from the LOWER
// keyframe. Returns a fresh grade clone. `fallback` is used when there are no
// keyframes at all.
export function gradeAtTime(keyframes, t, fallback) {
  const kfs = sortKeyframes(keyframes);
  if (!kfs.length) return clone(fallback || DEFAULT_GRADE);
  if (kfs.length === 1 || t <= kfs[0].t) return clone(kfs[0].grade);
  const last = kfs[kfs.length - 1];
  if (t >= last.t) return clone(last.grade);
  let i = 0;
  while (i < kfs.length - 1 && kfs[i + 1].t <= t) i += 1;
  const a = kfs[i];
  const b = kfs[i + 1];
  const span = b.t - a.t;
  const frac = span > 1e-6 ? (t - a.t) / span : 0;
  const blended = blendGrade(a.grade, b.grade, frac);
  // Spatial regions don't lerp — hold them from the lower keyframe.
  blended.masks = clone(a.grade.masks || []);
  blended.heal = clone(a.grade.heal || []);
  return blended;
}

// Which independent layers actually carry a non-identity adjustment, so the UI
// can show a dot / disable empty toggles.
export function activeLayers(grade) {
  const g = grade || DEFAULT_GRADE;
  const sig = (a, b) => gradeSignature(composeGrade(g, a)) !== gradeSignature(composeGrade(g, b));
  return {
    light: sig({ light: true, color: false, effects: false }, { light: false, color: false, effects: false }),
    color: sig({ light: false, color: true, effects: false }, { light: false, color: false, effects: false }),
    effects: sig({ light: false, color: false, effects: true }, { light: false, color: false, effects: false }),
  };
}

// ---- auto enhance ---------------------------------------------------------
// Derive a sensible "standard improvement" grade from a frame's histogram
// (the {r,g,b,l,bins} GradedCanvas emits via onStats). Strategy:
//   - exposure: nudge mean luma toward a pleasing midpoint
//   - contrast / whites / blacks: stretch a flat tonal range toward full black
//     and white without clipping
//   - white balance: gray-world neutralization of a color cast
//   - a gentle vibrance + clarity "pop" that suits surf footage
// It's intentionally conservative so it reads as a tasteful baseline, not a
// gimmick. `mode` = 'all' | 'light' | 'color'.
function histMean(h, bins) {
  let s = 0;
  let c = 0;
  for (let i = 0; i < bins; i += 1) { s += i * h[i]; c += h[i]; }
  return c ? s / c / (bins - 1) : 0.5;
}
function histPct(h, bins, p) {
  let total = 0;
  for (let i = 0; i < bins; i += 1) total += h[i];
  if (!total) return p;
  const target = total * p;
  let cum = 0;
  for (let i = 0; i < bins; i += 1) {
    cum += h[i];
    if (cum >= target) return i / (bins - 1);
  }
  return 1;
}
const clampN = (v, lo, hi) => Math.max(lo, Math.min(hi, Math.round(v)));

export function autoGrade(stats, base = DEFAULT_GRADE, mode = 'all') {
  const g = clone(base);
  if (!stats || !stats.l) return g;
  const { l, r, gg: gC, g: gAlt, b, bins = 64 } = stats;
  const green = gC || gAlt; // tolerate either key
  const meanL = histMean(l, bins);
  const lo = histPct(l, bins, 0.005);
  const hi = histPct(l, bins, 0.995);
  const spread = Math.max(0.05, hi - lo);

  if (mode === 'all' || mode === 'light') {
    g.basic = { ...g.basic };
    const ev = Math.log2(0.46 / Math.max(meanL, 0.04));
    g.basic.exposure = Math.round(Math.max(-1.1, Math.min(1.1, ev)) * 100) / 100;
    // CRUSH/CLIP-SAFE auto base (mirror of backend autoGrade). The median (meanL)
    // and the top (p90) survive the tonemap and are the honest "dark scene" vs
    // "bright scene" signal; shadow/clip MASS is suppressed by it. Cap contrast at
    // +16 and the black crush at -10, EASE both as the median drops, and protect a
    // hot top so the look stage can't blow the spray. The per-clip LOOK adds punch.
    const p90 = histPct(l, bins, 0.90);
    const darkEase = meanL < 0.45 ? Math.max(0.3, meanL / 0.45) : 1;
    const brightTop = Math.max(0, p90 - 0.7);
    g.basic.contrast = Math.round(clampN((0.82 - spread) * 130, 0, 16) * darkEase);
    g.basic.whites = clampN((0.97 - hi) * 200, 0, 38);
    g.basic.blacks = Math.round(clampN(-(lo - 0.02) * 200, -10, 0) * darkEase);
    if (brightTop > 0) {
      g.basic.highlights = clampN(-(10 + brightTop * 130), -55, 0);
      g.basic.whites = clampN(Math.min(g.basic.whites, 18 - brightTop * 130), -8, 38);
    } else if (hi >= 0.995 && p90 > 0.92) {
      g.basic.highlights = -14;
    }
  }

  if ((mode === 'all' || mode === 'color') && r && green && b) {
    const mr = histMean(r, bins);
    const mg = histMean(green, bins);
    const mb = histMean(b, bins);
    const avg = Math.max(0.04, (mr + mg + mb) / 3);
    g.wb = {
      temp: clampN(((mb - mr) / avg) * 130, -50, 50),
      tint: clampN(((mg - (mr + mb) / 2) / avg) * 130, -38, 38),
    };
    // Haze-aware dehaze, GATED ON CHROMA (mirror of backend autoGrade). A bright
    // clear turquoise face lifts the black floor like haze does but stays
    // saturated; without the chroma gate it got over-dehazed into flat electric
    // turquoise. Fade the boost out as chroma rises; ceiling pulled to 16.
    const chroma = (Math.max(mr, mg, mb) - Math.min(mr, mg, mb)) / avg;
    const hazeGate = Math.max(0, 1 - chroma / 0.22);
    const haze = Math.max(0, lo - 0.04) * hazeGate;
    g.presence = {
      ...g.presence,
      vibrance: Math.max(g.presence?.vibrance || 0, 10),
      clarity: Math.max(g.presence?.clarity || 0, clampN(8 + haze * 60, 8, 13)),
      dehaze: Math.max(g.presence?.dehaze || 0, clampN(8 + haze * 110, 8, 16)),
    };
  }
  return g;
}

// ---- small math (pure; mirrored in backend) -------------------------------
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const smoothstep = (e0, e1, x) => {
  const t = clamp01((x - e0) / (e1 - e0 || 1e-6));
  return t * t * (3 - 2 * t);
};
const lumaOf = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

function evalCurve(points, x) {
  if (!points || points.length < 2) return x;
  if (x <= points[0].x) return points[0].y;
  const last = points[points.length - 1];
  if (x >= last.x) return last.y;
  for (let i = 1; i < points.length; i += 1) {
    if (x <= points[i].x) {
      const a = points[i - 1];
      const b = points[i];
      const f = (x - a.x) / (b.x - a.x || 1e-6);
      return a.y + (b.y - a.y) * f;
    }
  }
  return last.y;
}

function rgb2hsv(r, g, b) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max;
  return [h, s, max];
}

function hsv2rgb(h, s, v) {
  const c = v * s;
  const hh = ((h % 360) + 360) % 360 / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hh < 1) [r, g, b] = [c, x, 0];
  else if (hh < 2) [r, g, b] = [x, c, 0];
  else if (hh < 3) [r, g, b] = [0, c, x];
  else if (hh < 4) [r, g, b] = [0, x, c];
  else if (hh < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const m = v - c;
  return [r + m, g + m, b + m];
}

// Circular gaussian weight of a hue against a band center.
function bandWeight(hue, center) {
  let d = Math.abs(hue - center);
  if (d > 180) d = 360 - d;
  const sigma = 30;
  return Math.exp(-(d * d) / (2 * sigma * sigma));
}

// Signed color-tint vector for a color-grading wheel ({h,s,l}).
function wheelTint(wheel) {
  const s = (wheel.s || 0) / 100;
  if (s === 0) return [0, 0, 0, (wheel.l || 0) / 100];
  const [r, g, b] = hsv2rgb(wheel.h || 0, 1, 1);
  const lum = lumaOf(r, g, b);
  return [(r - lum) * s, (g - lum) * s, (b - lum) * s, (wheel.l || 0) / 100];
}

// ---- input color management (camera log/gamut → display Rec.709) ----------
// Decode a normalized code value from a camera log curve to scene-linear.
function logToLinear(t, v) {
  const x = v < 0 ? 0 : v;
  switch (t) {
    case 'log3g10': // RED Log3G10 (IPP2)
      return (Math.pow(10, x / 0.224282) - 1) / 155.975327 - 0.01;
    case 'slog3': // Sony S-Log3
      return x >= 171.2102946929 / 1023
        ? (Math.pow(10, (x * 1023 - 420) / 261.5) * (0.18 + 0.01) - 0.01)
        : (x * 1023 - 95) * 0.01125000 / (171.2102946929 - 95);
    case 'clog3': // Canon C-Log3 (approx)
      return x < 0.097465473
        ? -(Math.pow(10, (0.12783901 - x) / 0.36726845) - 1) / 14.98325
        : (Math.pow(10, (x - 0.12240537) / 0.36726845) - 1) / 14.98325;
    case 'vlog': // Panasonic V-Log
      return x < 0.181
        ? (x - 0.125) / 5.6
        : Math.pow(10, (x - 0.598206) / 0.241514) - 0.00873;
    case 'hlg': { // BT.2100 HLG inverse OETF (scene-ish)
      const a = 0.17883277; const bb = 0.28466892; const cc = 0.55991073;
      return x <= 0.5 ? (x * x) / 3 : (Math.exp((x - cc) / a) + bb) / 12;
    }
    case 'linear':
      return x;
    default:
      return null; // srgb / unknown → no transform
  }
}

// ACES-ish filmic tonemap (scene-linear → display 0..1).
function filmicTonemap(x) {
  const v = x < 0 ? 0 : x;
  const y = (v * (2.51 * v + 0.03)) / (v * (2.43 * v + 0.59) + 0.14);
  return clamp01(y);
}

// RED Wide Gamut RGBv3 → Rec.709 (linear), approximate.
const RWG_TO_709 = [
  1.4124, -0.1789, -0.2335,
  -0.0786, 1.2848, -0.2062,
  -0.0023, -0.1295, 1.1318,
];

// ---- tunable display rendering transform (DRT) ----------------------------
// All three knobs default to the legacy values (medium/off/rec709), so a log
// decode with no `display` block produces byte-identical output to before.
const TONE_S = { off: 0, low: 0.5, medium: 1, high: 1.6 };
const ROLL_K = { off: 0, low: 0.18, medium: 0.34, high: 0.55 };

// Highlight roll-off knee: gently compress scene-linear above ~0.7 before the
// tone-map, taming clipped speculars (spray, sun). k=0 → identity.
function rolloffKnee(lin, k) {
  if (k <= 0) return lin;
  const knee = 0.7;
  if (lin <= knee) return lin;
  return knee + (lin - knee) / (1 + k * (lin - knee) * 3);
}

// Tone-map with selectable strength. s=1 (medium) === legacy filmicTonemap(lin);
// s=0 (off) clips linearly; s>1 (high) compresses highlights harder.
function toneMapS(lin, s) {
  if (s <= 0) return clamp01(lin);
  const f = filmicTonemap(lin * (0.6 + 0.4 * s));
  return s >= 1 ? f : clamp01(lin * (1 - s) + f * s);
}

// Display encoding. 'rec709' === legacy (identity); 'srgb' lifts shadows a touch
// to mimic the sRGB toe vs a pure 2.4 gamma — useful for stills bound for
// Lightroom on an sRGB monitor.
function outputEncode(y, mode) {
  if (mode === 'srgb') return clamp01(Math.pow(clamp01(y), 1 / 1.06));
  return y;
}

// Auto-exposure for camera-log footage. Given the median code value (0..1) of a
// RAW log frame, return the exposure (stops) that lands its de-logged mid-gray on
// `target` display grey — normalizing clips shot at different exposures so the
// IPP2 baseline reads consistently (the fix for flat/under-exposed RED looking
// crushed). Bisects the monotonic tone-map; clamped to a sane range.
export function autoExposureStops(rawMedian, transform = 'log3g10', target = 0.34) {
  const lin = logToLinear(transform, clamp01(rawMedian || 0));
  if (!(lin > 0)) return 0;
  const out = (st) => toneMapS(lin * Math.pow(2, st), 1);
  let lo = -3;
  let hi = 5;
  if (out(hi) <= target) return hi;
  if (out(lo) >= target) return lo;
  for (let i = 0; i < 24; i += 1) {
    const mid = (lo + hi) / 2;
    if (out(mid) < target) lo = mid; else hi = mid;
  }
  return Math.round(((lo + hi) / 2) * 100) / 100;
}

function applyInputTransform(r, gg, b, t, disp) {
  if (!t || t === 'srgb') return [r, gg, b];
  let lr = logToLinear(t, r);
  let lg = logToLinear(t, gg);
  let lb = logToLinear(t, b);
  if (lr == null) return [r, gg, b];
  // Gamut: RED Wide Gamut needs a matrix into Rec.709 primaries.
  if (t === 'log3g10') {
    const m = RWG_TO_709;
    const nr = m[0] * lr + m[1] * lg + m[2] * lb;
    const ng = m[3] * lr + m[4] * lg + m[5] * lb;
    const nb = m[6] * lr + m[7] * lg + m[8] * lb;
    lr = nr; lg = ng; lb = nb;
  }
  const d = disp || {};
  const k = ROLL_K[d.rolloff] ?? 0;
  const s = TONE_S[d.tonemap] ?? 1;
  const out = d.output || 'rec709';
  // Scene-linear exposure comp (stops) BEFORE the knee/tone-map — this is the
  // only place a lift restores shadow detail rather than just greying it out.
  const ev = d.exposure ? Math.pow(2, d.exposure) : 1;
  if (ev !== 1) { lr *= ev; lg *= ev; lb *= ev; }
  return [
    outputEncode(toneMapS(rolloffKnee(lr, k), s), out),
    outputEncode(toneMapS(rolloffKnee(lg, k), s), out),
    outputEncode(toneMapS(rolloffKnee(lb, k), s), out),
  ];
}

// ---- secondary "color warper" curves --------------------------------------
// Evaluate around a 0.5-neutral curve: returns the signed delta (y-0.5)*2.
function curveDelta(points, x) {
  if (!points || points.length < 2) return 0;
  return (evalCurve(points, x) - 0.5) * 2;
}
function anyHsCurve(h) {
  return !!(h && (h.hueHue || h.hueSat || h.hueLum || h.satSat || h.lumSat));
}
// Apply hue/sat/lum secondary curves to an rgb pixel. Mirrored in backend.
function applyHsCurves(r, gg, b, hc) {
  if (!anyHsCurve(hc)) return [r, gg, b];
  let [h, s, v] = rgb2hsv(clamp01(r), clamp01(gg), clamp01(b));
  const hx = h / 360;
  if (hc.hueHue) h = (h + curveDelta(hc.hueHue, hx) * 180 + 360) % 360; // ±180° shift
  if (hc.hueSat) s = clamp01(s * (1 + curveDelta(hc.hueSat, hx)));       // hue→sat
  if (hc.hueLum) v = clamp01(v * (1 + curveDelta(hc.hueLum, hx) * 0.6)); // hue→luma
  if (hc.satSat) s = clamp01(s * (1 + curveDelta(hc.satSat, s)));        // sat→sat
  if (hc.lumSat) s = clamp01(s * (1 + curveDelta(hc.lumSat, v)));        // luma→sat
  return hsv2rgb(h, s, v);
}

// ---- the pipeline (operates on normalized sRGB-ish rgb) -------------------
export function applyGradePixel(r0, g0, b0, grade) {
  const g = grade;
  let r = r0;
  let gg = g0;
  let b = b0;

  // Input color science: decode camera log/gamut to display Rec.709 first.
  if (g.inputTransform && g.inputTransform !== 'srgb') {
    [r, gg, b] = applyInputTransform(r, gg, b, g.inputTransform, g.display);
  }

  // Profile base curve.
  if (g.profile === 'flat') {
    r = 0.12 + r * 0.76; gg = 0.12 + gg * 0.76; b = 0.12 + b * 0.76;
  } else if (g.profile === 'adobe-color') {
    // Gentle S-curve for a pleasing default contrast.
    const sc = (v) => clamp01(v + 0.12 * (v - 0.5) - 0.5 * 0.12 * 0 + 0.12 * Math.sin((v - 0.5) * Math.PI) * 0.5);
    r = sc(r); gg = sc(gg); b = sc(b);
  }

  // White balance.
  const tF = (g.wb.temp || 0) / 100;
  const tnF = (g.wb.tint || 0) / 100;
  r *= 1 + 0.20 * tF + 0.10 * tnF;
  gg *= 1 - 0.15 * tnF;
  b *= 1 - 0.20 * tF + 0.10 * tnF;

  // Exposure (stops).
  const ev = Math.pow(2, g.basic.exposure || 0);
  r *= ev; gg *= ev; b *= ev;

  // Contrast (pivot 0.5).
  const c = 1 + (g.basic.contrast || 0) / 100;
  r = (r - 0.5) * c + 0.5; gg = (gg - 0.5) * c + 0.5; b = (b - 0.5) * c + 0.5;

  // Highlights / Shadows / Whites / Blacks (per-channel region weights).
  const hl = (g.basic.highlights || 0) / 100;
  const sh = (g.basic.shadows || 0) / 100;
  const wh = (g.basic.whites || 0) / 100;
  const bl = (g.basic.blacks || 0) / 100;
  const region = (v) => {
    let x = v;
    x += hl * 0.5 * smoothstep(0.5, 1.0, x);
    x += sh * 0.5 * (1 - smoothstep(0.0, 0.5, x));
    x += wh * 0.3 * smoothstep(0.6, 1.0, x);
    x += bl * 0.3 * (1 - smoothstep(0.0, 0.4, x));
    return x;
  };
  r = region(r); gg = region(gg); b = region(b);

  // Dehaze (tonal part: contrast + lift in low-luma haze). Spatial part is separate.
  const dh = (g.presence.dehaze || 0) / 100;
  if (dh !== 0) {
    const cc = 1 + dh * 0.4;
    r = (r - 0.5) * cc + 0.5; gg = (gg - 0.5) * cc + 0.5; b = (b - 0.5) * cc + 0.5;
  }

  r = clamp01(r); gg = clamp01(gg); b = clamp01(b);

  // Tone curves (master then per-channel).
  if (g.curve) {
    if (g.curve.master) { r = evalCurve(g.curve.master, r); gg = evalCurve(g.curve.master, gg); b = evalCurve(g.curve.master, b); }
    if (g.curve.red) r = evalCurve(g.curve.red, r);
    if (g.curve.green) gg = evalCurve(g.curve.green, gg);
    if (g.curve.blue) b = evalCurve(g.curve.blue, b);
  }

  // HSL / Color Mixer (8 bands).
  let hslActive = false;
  for (let i = 0; i < HSL_BANDS; i += 1) {
    const band = g.hsl[i];
    if (band && (band.h || band.s || band.l)) { hslActive = true; break; }
  }
  if (hslActive) {
    let [h, s, v] = rgb2hsv(r, gg, b);
    let dHue = 0; let dSat = 0; let dL = 0; let wsum = 0;
    for (let i = 0; i < HSL_BANDS; i += 1) {
      const w = bandWeight(h, BAND_HUE[i]);
      const band = g.hsl[i];
      dHue += w * (band.h || 0); dSat += w * (band.s || 0); dL += w * (band.l || 0); wsum += w;
    }
    if (wsum > 0) {
      h += (dHue / wsum) * 0.3;
      s = clamp01(s * (1 + (dSat / wsum) / 100));
      v = clamp01(v * (1 + (dL / wsum) / 100 * 0.5));
    }
    [r, gg, b] = hsv2rgb(h, s, v);
  }

  // Secondary "color warper" curves (hue/sat/luma relationships).
  if (anyHsCurve(g.hsCurves)) {
    [r, gg, b] = applyHsCurves(r, gg, b, g.hsCurves);
  }

  // Color grading wheels (tonal region tints + luma offsets).
  const cg = g.colorGrading;
  if (cg) {
    const L = lumaOf(r, gg, b);
    const wShadow = 1 - smoothstep(0.0, 0.5, L);
    const wHigh = smoothstep(0.5, 1.0, L);
    const wMid = 1 - Math.abs(L - 0.5) * 2;
    const regions = [
      [cg.shadows, wShadow],
      [cg.midtones, wMid < 0 ? 0 : wMid],
      [cg.highlights, wHigh],
      [cg.global, 1],
    ];
    for (const [wheel, weight] of regions) {
      if (!wheel || (!wheel.h && !wheel.s && !wheel.l)) continue;
      const [tr, tg, tb, tl] = wheelTint(wheel);
      r += tr * weight * 0.5; gg += tg * weight * 0.5; b += tb * weight * 0.5;
      const lo = tl * weight * 0.25;
      r += lo; gg += lo; b += lo;
    }
  }

  // Vibrance + Saturation (with optional skin-tone protection).
  const sat = (g.presence.saturation || 0) / 100;
  const vib = (g.presence.vibrance || 0) / 100;
  if (sat !== 0 || vib !== 0) {
    const L = lumaOf(r, gg, b);
    const [pxHue, pxSat] = rgb2hsv(clamp01(r), clamp01(gg), clamp01(b));
    let f = 1 + sat;
    f += vib * (1 - pxSat);
    // Hold skin/orange hues (~15–50°) back so faces stay natural while the
    // water/sky pop. skinProtect 0..100 → up to ~85% reduction at hue center.
    const sp = (g.presence.skinProtect || 0) / 100;
    if (sp > 0 && f > 1) {
      const skinW = bandWeight(pxHue, 30); // gaussian around 30°
      f = 1 + (f - 1) * (1 - 0.85 * sp * skinW);
    }
    r = L + (r - L) * f; gg = L + (gg - L) * f; b = L + (b - L) * f;
  }

  return [clamp01(r), clamp01(gg), clamp01(b)];
}

// Bake the point pipeline into a size^3 RGB LUT (Float32Array, length size^3*3).
// Layout matches a .cube file: r fastest, then g, then b.
export function compileLut(grade, size = 33) {
  const out = new Float32Array(size * size * size * 3);
  const n = size - 1;
  let i = 0;
  for (let bi = 0; bi < size; bi += 1) {
    for (let gi = 0; gi < size; gi += 1) {
      for (let ri = 0; ri < size; ri += 1) {
        const [r, g, b] = applyGradePixel(ri / n, gi / n, bi / n, grade);
        out[i] = r; out[i + 1] = g; out[i + 2] = b;
        i += 3;
      }
    }
  }
  return out;
}

// Composite LUT for two stacked grades, applied bottom-then-top — exactly the
// order the renderer bakes (per-clip look first, movie-wide look on top). Used
// by the studio's "as published" preview so it matches the final file.
export function compileLutPair(bottom, top, size = 33) {
  const out = new Float32Array(size * size * size * 3);
  const n = size - 1;
  let i = 0;
  for (let bi = 0; bi < size; bi += 1) {
    for (let gi = 0; gi < size; gi += 1) {
      for (let ri = 0; ri < size; ri += 1) {
        const [r0, g0, b0] = applyGradePixel(ri / n, gi / n, bi / n, bottom);
        const [r, g, b] = applyGradePixel(r0, g0, b0, top);
        out[i] = r; out[i + 1] = g; out[i + 2] = b;
        i += 3;
      }
    }
  }
  return out;
}

// --- Imported creative LUTs (.cube) --------------------------------------
// A creative 3D LUT applied AFTER the technical/creative grade, as a second
// lut3d stage (GLSL preview + ffmpeg bake). We normalize every import to a
// 33³ grid stored compactly (Uint16, red-fastest) on the grade as
// grade.lut = { name, size:33, data:<base64> } so the doc stays small and the
// backend can rebuild a .cube for ffmpeg with no extra storage/endpoints.
export const LUT_IMPORT_SIZE = 33;

// Parse Adobe .cube text → { size, data:Float32Array(size³*3) } red-fastest.
export function parseCubeText(text) {
  if (!text || typeof text !== 'string') return null;
  let size = 0;
  const vals = [];
  const lines = text.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    if (/^TITLE/i.test(line) || /^DOMAIN_/i.test(line)) continue;
    const m = line.match(/^LUT_3D_SIZE\s+(\d+)/i);
    if (m) { size = parseInt(m[1], 10); continue; }
    if (/^LUT_1D_SIZE/i.test(line)) return null; // 1D LUTs not supported
    const parts = line.split(/\s+/).map(Number);
    if (parts.length >= 3 && parts.every((x) => Number.isFinite(x))) {
      vals.push(parts[0], parts[1], parts[2]);
    }
  }
  if (!size || vals.length !== size * size * size * 3) return null;
  return { size, data: Float32Array.from(vals) };
}

const sampleLut = (data, size, r, g, b) => {
  // Nearest-grid lookup (used inside trilinear resample); clamps to edges.
  const c = (v) => Math.max(0, Math.min(size - 1, Math.round(v * (size - 1))));
  const ri = c(r); const gi = c(g); const bi = c(b);
  const idx = ((bi * size + gi) * size + ri) * 3;
  return [data[idx], data[idx + 1], data[idx + 2]];
};

// Trilinear resample of an arbitrary-size LUT to `target`³ (default 33).
export function resampleLut(data, size, target = LUT_IMPORT_SIZE) {
  if (size === target) return data;
  const out = new Float32Array(target * target * target * 3);
  const n = target - 1;
  const s1 = size - 1;
  let i = 0;
  for (let bi = 0; bi < target; bi += 1) {
    for (let gi = 0; gi < target; gi += 1) {
      for (let ri = 0; ri < target; ri += 1) {
        const fr = (ri / n) * s1; const fg = (gi / n) * s1; const fb = (bi / n) * s1;
        const r0 = Math.floor(fr); const g0 = Math.floor(fg); const b0 = Math.floor(fb);
        const r1 = Math.min(s1, r0 + 1); const g1 = Math.min(s1, g0 + 1); const b1 = Math.min(s1, b0 + 1);
        const dr = fr - r0; const dg = fg - g0; const db = fb - b0;
        const at = (rr, gg, bb) => {
          const idx = ((bb * size + gg) * size + rr) * 3;
          return [data[idx], data[idx + 1], data[idx + 2]];
        };
        const lerp = (a, c, t) => a + (c - a) * t;
        const mix3 = (A, B, t) => [lerp(A[0], B[0], t), lerp(A[1], B[1], t), lerp(A[2], B[2], t)];
        const c00 = mix3(at(r0, g0, b0), at(r1, g0, b0), dr);
        const c10 = mix3(at(r0, g1, b0), at(r1, g1, b0), dr);
        const c01 = mix3(at(r0, g0, b1), at(r1, g0, b1), dr);
        const c11 = mix3(at(r0, g1, b1), at(r1, g1, b1), dr);
        const c0 = mix3(c00, c10, dg);
        const c1 = mix3(c01, c11, dg);
        const c = mix3(c0, c1, db);
        out[i] = c[0]; out[i + 1] = c[1]; out[i + 2] = c[2];
        i += 3;
      }
    }
  }
  return out;
};

// Float LUT (0..1) → compact base64 Uint16 (and back) for storage on the grade.
export function lutToStored(data) {
  const u = new Uint16Array(data.length);
  for (let i = 0; i < data.length; i += 1) {
    u[i] = Math.max(0, Math.min(65535, Math.round(data[i] * 65535)));
  }
  const bytes = new Uint8Array(u.buffer);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}
export function lutFromStored(b64) {
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    const u = new Uint16Array(bytes.buffer);
    const f = new Float32Array(u.length);
    for (let i = 0; i < u.length; i += 1) f[i] = u[i] / 65535;
    return f;
  } catch {
    return null;
  }
}

// Build a grade.lut object from raw .cube text (parse → resample 33 → pack).
export function lutFromCubeText(text, name) {
  const parsed = parseCubeText(text);
  if (!parsed) return null;
  const data = resampleLut(parsed.data, parsed.size, LUT_IMPORT_SIZE);
  const id = `${(name || 'lut').replace(/[^a-z0-9]/gi, '').slice(0, 16)}_${Date.now().toString(36)}`;
  return { id, name: name || 'Imported LUT', size: LUT_IMPORT_SIZE, data: lutToStored(data) };
}

// Neutral base: the display transform only (input decode + DRT), with no
// creative grade and no profile contrast curve. This is the clean,
// display-referred "negative" you'd hand to Lightroom (or bake as a .cube to
// start a Resolve grade from).
export function baseGrade(grade) {
  const g = grade || DEFAULT_GRADE;
  const d = g.display || DEFAULT_GRADE.display;
  return {
    ...clone(DEFAULT_GRADE),
    inputTransform: g.inputTransform || 'srgb',
    // STABLE clean decode: keep only the camera de-log essentials — the per-clip
    // auto-exposure normalization and the output encoding — but always use the
    // DEFAULT tone-map / roll-off. The tone-map/roll-off are creative DRT choices
    // that belong to the final look, not the neutral base; inheriting them made
    // the neutral preview change (and stay "baked") with every preset, so it never
    // returned to the same clean base. Now "view neutral" is identical regardless
    // of the creative layer on top — i.e. fully reversible.
    display: {
      ...clone(DEFAULT_GRADE.display),
      exposure: d.exposure ?? DEFAULT_GRADE.display.exposure,
      output: d.output || DEFAULT_GRADE.display.output,
    },
    profile: 'neutral',
  };
}

// A TRUE "back to original" reset — the target of the None style chip and the
// Reset buttons. For display footage (sRGB) this is the exact DEFAULT_GRADE, so
// the grade reads as identity and the renderer publishes the raw source
// untouched. For camera-log footage (RED etc.) we keep only the decode
// essentials — input transform, per-clip auto-exposure and output encoding — on
// a NEUTRAL profile. DEFAULT_GRADE's 'adobe-color' profile bakes a contrast
// S-curve, which is invisible at identity (the renderer skips identity grades)
// but becomes a baked-in residual the moment the decode keeps the grade active;
// that residual is exactly the "None doesn't reset" bug.
export function neutralLook(grade) {
  const cur = grade || DEFAULT_GRADE;
  if (!cur.inputTransform || cur.inputTransform === 'srgb') return clone(DEFAULT_GRADE);
  const next = clone(DEFAULT_GRADE);
  next.inputTransform = cur.inputTransform;
  next.display = {
    ...clone(DEFAULT_GRADE.display),
    exposure: cur.display?.exposure ?? DEFAULT_GRADE.display.exposure,
    output: cur.display?.output || DEFAULT_GRADE.display.output,
  };
  next.profile = 'neutral';
  return next;
}

// The CREATIVE LOOK in isolation — the exact transform that maps the *neutral
// base* (already de-logged + display-mapped) to the final graded image. Because
// the pipeline applies the de-log/display first and every creative op after, a
// grade with inputTransform='srgb' replays just the creative half on a
// display-referred input. We also zero Exposure & White Balance so the look is
// reusable across clips: the intended workflow is "set exposure + WB per clip,
// THEN apply this look." Use this for a look LUT that pairs with the neutral
// base, or to drive a Lightroom develop preset.
export function creativeLookGrade(grade) {
  const g = clone(grade || DEFAULT_GRADE);
  g.inputTransform = 'srgb';
  g.display = clone(DEFAULT_GRADE.display);
  g.basic = { ...g.basic, exposure: 0 };
  g.wb = { temp: 0, tint: 0 };
  return g;
}

// Serialize the point pipeline to .cube LUT text (red fastest) — mirror of
// backend/api/grade.js cubeText so the browser can offer a direct download for
// Resolve / Premiere / Photoshop. Spatial ops (clarity/grain/etc.) are NOT in
// the LUT, matching the render path.
export function cubeText(grade, size = 33, title = '') {
  const n = size - 1;
  const lines = [];
  if (title) lines.push(`TITLE "${String(title).replace(/"/g, "'")}"`);
  lines.push(`LUT_3D_SIZE ${size}`, 'DOMAIN_MIN 0 0 0', 'DOMAIN_MAX 1 1 1');
  for (let bi = 0; bi < size; bi += 1) {
    for (let gi = 0; gi < size; gi += 1) {
      for (let ri = 0; ri < size; ri += 1) {
        const [r, g, b] = applyGradePixel(ri / n, gi / n, bi / n, grade);
        lines.push(`${r.toFixed(5)} ${g.toFixed(5)} ${b.toFixed(5)}`);
      }
    }
  }
  return lines.join('\n');
}

// Spatial params consumed by the WebGL shader / ffmpeg (post-LUT).
export function spatialParams(grade) {
  return {
    clarity: (grade.presence?.clarity || 0) / 100,
    texture: (grade.presence?.texture || 0) / 100,
    dehaze: (grade.presence?.dehaze || 0) / 100,
    denoise: (grade.presence?.denoise || 0) / 100,
    sharpness: (grade.effects?.sharpness || 0) / 100,
    vignette: (grade.effects?.vignette || 0) / 100,
    grain: (grade.effects?.grain || 0) / 100,
    halation: (grade.effects?.halation || 0) / 100,
    glow: (grade.effects?.glow || 0) / 100,
  };
}

const num = (v) => Math.round((v || 0) * 1000) / 1000;
export function gradeSignature(grade) {
  if (!grade) return 'identity';
  const g = grade;
  return JSON.stringify({
    p: g.profile, it: g.inputTransform, dp: g.display,
    wb: g.wb, ba: g.basic, pr: g.presence,
    cu: g.curve, hc: g.hsCurves, hs: g.hsl, cg: g.colorGrading, ef: g.effects, mk: g.masks, hl: g.heal,
  }, (k, v) => (typeof v === 'number' ? num(v) : v));
}

// Key-order-canonical variant for signatures that survive a Firestore
// round-trip: Firestore re-serializes maps with its own (alphabetical) key
// order, so plain JSON.stringify of the same grade can differ between the
// object the UI built and the one the worker read back — a false "changed"
// signal. Sorting keys recursively makes the signature depend only on CONTENT.
// Used where a stored signature is compared against a locally computed one
// (e.g. the HQ segment preview's staleness check). Keep in sync with
// backend/api/grade.js.
const canonValue = (v) => {
  if (Array.isArray(v)) return v.map(canonValue);
  if (v && typeof v === 'object') {
    const o = {};
    for (const k of Object.keys(v).sort()) o[k] = canonValue(v[k]);
    return o;
  }
  return typeof v === 'number' ? num(v) : v;
};
export function gradeSignatureStable(grade) {
  if (!grade) return 'identity';
  const g = grade;
  return JSON.stringify(canonValue({
    p: g.profile, it: g.inputTransform, dp: g.display,
    wb: g.wb, ba: g.basic, pr: g.presence,
    cu: g.curve, hc: g.hsCurves, hs: g.hsl, cg: g.colorGrading, ef: g.effects, mk: g.masks, hl: g.heal,
  }));
}

export function isIdentityGrade(grade) {
  return !grade || gradeSignature(grade) === gradeSignature(DEFAULT_GRADE);
}

// ---- adjustment line-item introspection -----------------------------------
// A flat descriptor of every tunable scalar in the grade, so the UI can list
// exactly the line items that are currently doing something (e.g. what a preset
// changed) and offer a slider for each — without the user hunting through the
// full panel. Each entry: { path, label, min, max, step, unit }.
const ADJ_BANDS = ['Red', 'Orange', 'Yellow', 'Green', 'Aqua', 'Blue', 'Purple', 'Magenta'];
const ADJ_REGION = { shadows: 'Shadows', midtones: 'Midtones', highlights: 'Highlights', global: 'Global' };
const ADJ_DEFS = (() => {
  const defs = [
    { path: ['wb', 'temp'], label: 'Temp' },
    { path: ['wb', 'tint'], label: 'Tint' },
    { path: ['basic', 'exposure'], label: 'Exposure', min: -5, max: 5, step: 0.05, unit: 'EV' },
    { path: ['basic', 'contrast'], label: 'Contrast' },
    { path: ['basic', 'highlights'], label: 'Highlights' },
    { path: ['basic', 'shadows'], label: 'Shadows' },
    { path: ['basic', 'whites'], label: 'Whites' },
    { path: ['basic', 'blacks'], label: 'Blacks' },
    { path: ['presence', 'texture'], label: 'Texture' },
    { path: ['presence', 'clarity'], label: 'Clarity' },
    { path: ['presence', 'dehaze'], label: 'Dehaze' },
    { path: ['presence', 'vibrance'], label: 'Vibrance' },
    { path: ['presence', 'saturation'], label: 'Saturation' },
    { path: ['presence', 'skinProtect'], label: 'Skin protect', min: 0, max: 100 },
    { path: ['presence', 'denoise'], label: 'Noise reduction', min: 0, max: 100 },
    { path: ['effects', 'vignette'], label: 'Vignette', min: 0, max: 100 },
    { path: ['effects', 'grain'], label: 'Grain', min: 0, max: 100 },
    { path: ['effects', 'sharpness'], label: 'Sharpening', min: 0, max: 100 },
    { path: ['effects', 'glow'], label: 'Glow', min: 0, max: 100 },
    { path: ['effects', 'halation'], label: 'Halation', min: 0, max: 100 },
    { path: ['display', 'exposure'], label: 'Decode exposure', min: -5, max: 5, step: 0.05, unit: 'EV' },
  ];
  for (const r of ['shadows', 'midtones', 'highlights', 'global']) {
    defs.push({ path: ['colorGrading', r, 'h'], label: `${ADJ_REGION[r]} hue`, min: 0, max: 360 });
    defs.push({ path: ['colorGrading', r, 's'], label: `${ADJ_REGION[r]} sat` });
    defs.push({ path: ['colorGrading', r, 'l'], label: `${ADJ_REGION[r]} lum` });
  }
  for (let i = 0; i < HSL_BANDS; i += 1) {
    defs.push({ path: ['hsl', i, 'h'], label: `${ADJ_BANDS[i]} hue` });
    defs.push({ path: ['hsl', i, 's'], label: `${ADJ_BANDS[i]} sat` });
    defs.push({ path: ['hsl', i, 'l'], label: `${ADJ_BANDS[i]} lum` });
  }
  return defs;
})();

const getPath = (obj, path) => {
  let o = obj;
  for (const k of path) { if (o == null) return undefined; o = o[k]; }
  return o;
};

// Immutable deep-set at a path (array indices allowed). Returns a new grade
// (clone-then-mutate; every intermediate exists because grades are DEFAULT-shaped).
export function setPath(grade, path, value) {
  const root = clone(grade || DEFAULT_GRADE);
  let o = root;
  for (let i = 0; i < path.length - 1; i += 1) {
    if (o[path[i]] == null) o[path[i]] = typeof path[i + 1] === 'number' ? [] : {};
    o = o[path[i]];
  }
  o[path[path.length - 1]] = value;
  return root;
}

// The full set of numeric controls the color assistant (and line-item editor)
// can drive: each entry is a stable key, its grade path, label, range + default.
export const ADJUSTMENT_SCHEMA = ADJ_DEFS.map((d) => ({
  key: d.path.join('.'),
  path: d.path,
  label: d.label,
  min: d.min ?? -100,
  max: d.max ?? 100,
  step: d.step ?? 1,
  unit: d.unit || '',
  default: getPath(DEFAULT_GRADE, d.path) ?? 0,
}));
const SCHEMA_BY_KEY = new Map(ADJUSTMENT_SCHEMA.map((s) => [s.key, s]));

// Apply assistant ops [{key|path, value}] to a grade, each clamped to its
// control's range. Unknown keys are skipped. Returns a new grade.
export function applyGradeOps(grade, ops) {
  let g = clone(grade || DEFAULT_GRADE);
  for (const op of ops || []) {
    const key = op.key || (Array.isArray(op.path) ? op.path.join('.') : null);
    const def = key && SCHEMA_BY_KEY.get(key);
    if (!def) continue;
    let v = Number(op.value);
    if (!Number.isFinite(v)) continue;
    v = Math.max(def.min, Math.min(def.max, v));
    g = setPath(g, def.path, v);
  }
  return g;
}

// ---- clip grading sessions: hybrid grade composition ----------------------
// A proof's final grade composes from three layers so each frame's auto-grade
// (its own light/colour) survives clip-wide feedback, idempotently:
//   composeProofGrade(autoGradeBase, clipLayer, override)
// `clipLayer`/`override` are { ops:[{key,delta}], crop }. Ops are DELTAS added
// onto the base then clamped. Two pseudo-keys drive the auto water mask:
// `waterMask.saturation` (teal pop) and `waterMask.amount`. crop is a whole-rect
// replacement (zoom intent): override wins, then clipLayer, then base proof crop.
// KEEP IN SYNC with backend/shared/clipGrade.js (preview == bake).

// The delta-able controls sent to the AI for clip feedback: every numeric grade
// control plus the two water-mask pseudo-keys.
export const CLIP_DELTA_SCHEMA = [
  ...ADJUSTMENT_SCHEMA.map((s) => ({ key: s.key, label: s.label, min: s.min, max: s.max, unit: s.unit })),
  { key: 'waterMask.saturation', label: 'Water teal (mask saturation)', min: -100, max: 100, unit: '' },
  { key: 'waterMask.amount', label: 'Water mask strength', min: 0, max: 1, unit: '' },
  { key: 'subjectMask.exposure', label: 'Subject lift (mask EV)', min: -2, max: 2, unit: '' },
  { key: 'subjectMask.saturation', label: 'Subject mask saturation', min: -100, max: 100, unit: '' },
  { key: 'subjectMask.amount', label: 'Subject mask strength', min: 0, max: 1, unit: '' },
];

function applyDeltaOp(g, op) {
  const key = op && op.key;
  const delta = Number(op && op.delta);
  if (!key || !Number.isFinite(delta) || delta === 0) return g;
  if (key === 'waterMask.saturation' || key === 'waterMask.amount') {
    const masks = (g.masks || []).map((m) => (m ? { ...m } : m));
    const mask = masks.find((m) => m && m.source === 'auto-water');
    if (!mask) return g;
    if (key === 'waterMask.saturation') {
      mask.adj = { ...(mask.adj || {}) };
      mask.adj.saturation = Math.max(-100, Math.min(100, (mask.adj.saturation || 0) + delta));
    } else {
      mask.amount = Math.max(0, Math.min(1, (mask.amount == null ? 1 : mask.amount) + delta));
    }
    return { ...g, masks };
  }
  if (key === 'subjectMask.exposure' || key === 'subjectMask.amount' || key === 'subjectMask.saturation') {
    const masks = (g.masks || []).map((m) => (m ? { ...m } : m));
    const mask = masks.find((m) => m && m.source === 'auto-subject');
    if (!mask) return g;
    mask.adj = { ...(mask.adj || {}) };
    if (key === 'subjectMask.exposure') mask.adj.exposure = Math.max(-2, Math.min(2, (mask.adj.exposure || 0) + delta));
    else if (key === 'subjectMask.saturation') mask.adj.saturation = Math.max(-100, Math.min(100, (mask.adj.saturation || 0) + delta));
    else mask.amount = Math.max(0, Math.min(1, (mask.amount == null ? 1 : mask.amount) + delta));
    return { ...g, masks };
  }
  const def = SCHEMA_BY_KEY.get(key);
  if (!def) return g;
  const cur = Number(getPath(g, def.path)) || 0;
  return setPath(g, def.path, Math.max(def.min, Math.min(def.max, cur + delta)));
}

// Tasteful color ceilings applied AFTER all layers compose — keeps auto bakes
// photographic by capping chroma INTENSITY (the AI clip/override layer + auto
// water-mask can otherwise stack saturation into cartoon turquoise). Tonal
// controls are untouched. KEEP IN SYNC with tameProofColor in
// backend/shared/clipGrade.js.
const PROOF_REGIONS = ['shadows', 'midtones', 'highlights', 'global'];
export function tameProofColor(grade) {
  if (!grade || typeof grade !== 'object') return grade;
  const CEIL = gradingConfig().ceil; // live caps from Firestore (config/grading)
  const g = clone(grade);
  const cap = (v, hi) => (typeof v === 'number' && v > hi ? hi : v);
  const band = (v, hi) => (typeof v === 'number' ? Math.max(-hi, Math.min(hi, v)) : v);
  if (g.presence) {
    g.presence.vibrance = cap(g.presence.vibrance, CEIL.vibrance);
    g.presence.saturation = cap(g.presence.saturation, CEIL.saturation);
    g.presence.dehaze = cap(g.presence.dehaze, CEIL.dehaze);
    g.presence.clarity = cap(g.presence.clarity, CEIL.clarity);
  }
  if (Array.isArray(g.hsl)) for (const b of g.hsl) { if (b) b.s = band(b.s, CEIL.hslSat); }
  if (g.colorGrading) for (const r of PROOF_REGIONS) { const n = g.colorGrading[r]; if (n) n.s = band(n.s, CEIL.wheelSat); }
  if (Array.isArray(g.masks)) for (const m of g.masks) {
    if (!m) continue;
    if (m.source === 'auto-water') {
      if (m.adj) m.adj.saturation = band(m.adj.saturation, CEIL.waterSat);
      if (typeof m.amount === 'number') m.amount = Math.max(0, Math.min(CEIL.waterAmt, m.amount));
    } else if (m.source === 'auto-subject') {
      if (m.adj) m.adj.exposure = cap(m.adj.exposure, CEIL.subjectEv);
      if (typeof m.amount === 'number') m.amount = Math.max(0, Math.min(CEIL.subjectAmt, m.amount));
    }
  }
  return g;
}

// Compose the final absolute grade for a proof from its frozen auto base plus
// the clip-wide layer then any per-proof override (deltas applied in that order).
export function composeProofGrade(autoGradeBase, clipLayer, override) {
  let g = clone(autoGradeBase || DEFAULT_GRADE);
  const ops = [...((clipLayer && clipLayer.ops) || []), ...((override && override.ops) || [])];
  for (const op of ops) g = applyDeltaOp(g, op);
  return tameProofColor(g);
}

const validCropRect = (c) => c && Number(c.w) > 0 && Number(c.h) > 0;

// Resolve the crop for a proof: per-proof override wins, then the clip zoom
// intent, then the proof's own base crop, else full frame (null).
export function resolveProofCrop(baseCrop, clipLayer, override) {
  if (override && validCropRect(override.crop)) return override.crop;
  if (clipLayer && validCropRect(clipLayer.crop)) return clipLayer.crop;
  return validCropRect(baseCrop) ? baseCrop : null;
}

// The non-default scalar line items in this grade, each with its slider metadata.
export function activeAdjustments(grade) {
  const g = grade || DEFAULT_GRADE;
  const out = [];
  for (const d of ADJ_DEFS) {
    const v = getPath(g, d.path);
    if (v == null) continue;
    const def = getPath(DEFAULT_GRADE, d.path) ?? 0;
    if (Math.abs((v || 0) - (def || 0)) < 1e-6) continue;
    out.push({
      key: d.path.join('.'), path: d.path, label: d.label, value: v, def,
      min: d.min ?? -100, max: d.max ?? 100, step: d.step ?? 1, unit: d.unit || '',
    });
  }
  return out;
}

// Non-slider adjustments that are active (curves, masks, profile, log decode) —
// surfaced as short tags so the line-item list flags them even though they're
// edited in their own controls.
export function activeCurveNotes(grade) {
  const g = grade || DEFAULT_GRADE;
  const tags = [];
  if (g.curve && (g.curve.master || g.curve.red || g.curve.green || g.curve.blue)) tags.push('tone curve');
  if (anyHsCurve(g.hsCurves)) tags.push('secondary curves');
  const nm = activeMasks(g).length;
  if (nm) tags.push(`${nm} local mask${nm > 1 ? 's' : ''}`);
  if (g.profile && g.profile !== DEFAULT_GRADE.profile) tags.push(`${g.profile} profile`);
  if (g.inputTransform && g.inputTransform !== DEFAULT_GRADE.inputTransform) tags.push('log decode');
  return tags;
}

// Tag a grade with the preset/style it came from so the name + package travel
// with the edit (persisted on the clip/movie grade, shown in the review tree).
// `_preset` is pure metadata: gradeSignature() ignores it, so it never affects
// the LUT, equality, or publish/render signatures. `sig` stamps the signature of
// the APPLIED result, so once the user tweaks any slider the grade diverges and
// we can label it "edited" while keeping the lineage. Pass a falsy/`none` preset
// to clear the tag (e.g. on reset).
export function stampPreset(grade, preset, amount = 100) {
  const g = clone(grade || DEFAULT_GRADE);
  if (preset && (preset.id || preset.name) && preset.id !== 'none') {
    const amt = Math.max(0, Math.min(100, Math.round(amount)));
    g._preset = { id: preset.id || preset.name, name: preset.name || 'Preset', amount: amt, sig: gradeSignature(g) };
  } else {
    delete g._preset;
  }
  return g;
}

// The display label for a grade's preset lineage: the stamped name (with
// "· edited" once it diverges), else an exact signature match against the
// built-in styles, else null (a hand-built grade).
export function presetLabel(grade) {
  if (!grade) return null;
  const p = grade._preset;
  if (p && p.name) {
    const amt = p.amount != null && p.amount < 100 ? ` · ${p.amount}%` : '';
    const edited = gradeSignature(grade) === p.sig ? '' : ' · edited';
    return `${p.name}${amt}${edited}`;
  }
  const sig = gradeSignature(grade);
  const hit = PRESETS.find((pp) => pp.id !== 'none' && gradeSignature(pp.grade) === sig);
  return hit ? hit.name : null;
}

// Apply a "look" preset on top of the current decode. The creative presets below
// are authored in display Rec.709/sRGB (inputTransform 'srgb'). When the current
// footage is a camera log (e.g. RED Log3G10 / Wide Gamut), a display look MUST
// sit after the de-log — otherwise the look is graded onto a flat log image and
// reads washed-out with wrong color. So we carry the current input transform +
// display rendering transform onto the look. Presets that bring their own decode
// (inputTransform !== 'srgb', e.g. "RED IPP2 Natural") are applied unchanged.
export function applyLook(current, preset) {
  const next = clone(preset || DEFAULT_GRADE);
  const cur = current || DEFAULT_GRADE;
  const presetIsDisplay = !preset || !preset.inputTransform || preset.inputTransform === 'srgb';
  if (cur.inputTransform && cur.inputTransform !== 'srgb' && presetIsDisplay) {
    next.inputTransform = cur.inputTransform;
    // Carry the camera DECODE essentials onto the look — the input transform plus
    // the per-clip auto-exposure and output encoding — but let the preset bring
    // its OWN tone-map / roll-off (those are part of the look, e.g. Tucker's
    // highlight roll-off). Previously we kept the *entire* current display, which
    // made tone-map/roll-off "sticky": once set, no preset could change it and
    // even the neutral base (which keeps `display`) stayed baked — so switching
    // looks felt non-reversible. Falling back to the current display only when the
    // preset doesn't carry one keeps old single-look behavior intact.
    const presetDisplay = preset && preset.display ? preset.display : (cur.display || DEFAULT_GRADE.display);
    const curDisplay = cur.display || DEFAULT_GRADE.display;
    next.display = {
      ...clone(presetDisplay),
      exposure: curDisplay.exposure ?? DEFAULT_GRADE.display.exposure,
      output: curDisplay.output || DEFAULT_GRADE.display.output,
    };
  }
  return next;
}

// A few starter "Styles".
export const PRESETS = [
  { id: 'none', name: 'None', kind: 'any', grade: DEFAULT_GRADE },
  {
    id: 'teal-orange',
    name: 'Teal & Orange',
    kind: 'any',
    grade: {
      ...clone(DEFAULT_GRADE),
      basic: { ...DEFAULT_GRADE.basic, contrast: 12, shadows: 8, highlights: -10 },
      presence: { ...DEFAULT_GRADE.presence, vibrance: 18, clarity: 8 },
      colorGrading: {
        shadows: { h: 200, s: 22, l: -3 },
        midtones: { h: 0, s: 0, l: 0 },
        highlights: { h: 35, s: 18, l: 2 },
        global: { h: 0, s: 0, l: 0 },
        blending: 50,
      },
    },
  },
  {
    id: 'punchy-surf',
    name: 'Punchy Surf',
    kind: 'any',
    grade: {
      ...clone(DEFAULT_GRADE),
      wb: { temp: -6, tint: 2 },
      basic: { ...DEFAULT_GRADE.basic, contrast: 16, blacks: -8, whites: 6 },
      presence: { ...DEFAULT_GRADE.presence, vibrance: 24, clarity: 14, dehaze: 10 },
    },
  },
  {
    id: 'golden-hour',
    name: 'Golden Hour',
    kind: 'any',
    grade: {
      ...clone(DEFAULT_GRADE),
      wb: { temp: 18, tint: 6 },
      basic: { ...DEFAULT_GRADE.basic, highlights: -8, shadows: 10, contrast: 6 },
      presence: { ...DEFAULT_GRADE.presence, vibrance: 12 },
      colorGrading: {
        shadows: { h: 30, s: 10, l: 0 },
        midtones: { h: 40, s: 8, l: 0 },
        highlights: { h: 45, s: 14, l: 2 },
        global: { h: 0, s: 0, l: 0 },
        blending: 50,
      },
    },
  },
  {
    id: 'mono',
    name: 'Mono Film',
    kind: 'any',
    grade: {
      ...clone(DEFAULT_GRADE),
      basic: { ...DEFAULT_GRADE.basic, contrast: 18 },
      presence: { ...DEFAULT_GRADE.presence, saturation: -100, clarity: 10 },
      effects: { ...DEFAULT_GRADE.effects, grain: 22, vignette: 18 },
    },
  },
  {
    // De-flatten RED Log3G10/Wide Gamut into a natural Rec.709 baseline — the
    // starting point for grading monotone RED footage. Tune from here.
    id: 'red-ipp2',
    name: 'RED IPP2 Natural',
    kind: 'video',
    grade: {
      ...clone(DEFAULT_GRADE),
      inputTransform: 'log3g10',
      basic: { ...DEFAULT_GRADE.basic, contrast: 6, blacks: -6, whites: 6 },
      presence: { ...DEFAULT_GRADE.presence, vibrance: 16, saturation: 4, clarity: 6, skinProtect: 40 },
    },
  },
  {
    // Vibrant tropical water: push cyans/blues, keep skin natural.
    id: 'tropical-water',
    name: 'Tropical Water',
    kind: 'any',
    grade: {
      ...clone(DEFAULT_GRADE),
      wb: { temp: -4, tint: 2 },
      basic: { ...DEFAULT_GRADE.basic, contrast: 10, blacks: -6 },
      presence: { ...DEFAULT_GRADE.presence, vibrance: 26, clarity: 10, dehaze: 8, skinProtect: 55 },
      hsl: (() => {
        const h = Array.from({ length: HSL_BANDS }, () => ({ h: 0, s: 0, l: 0 }));
        h[4] = { h: -6, s: 26, l: 4 }; // aqua → push toward teal, brighter
        h[5] = { h: -4, s: 18, l: 2 }; // blue
        return h;
      })(),
    },
  },
  {
    // Cinematic film look with warm highlight halation + soft glow on spray.
    id: 'film-halation',
    name: 'Film + Halation',
    kind: 'any',
    grade: {
      ...clone(DEFAULT_GRADE),
      basic: { ...DEFAULT_GRADE.basic, contrast: 12, highlights: -8, shadows: 6 },
      presence: { ...DEFAULT_GRADE.presence, vibrance: 10, clarity: 4 },
      colorGrading: {
        shadows: { h: 205, s: 14, l: -2 },
        midtones: { h: 0, s: 0, l: 0 },
        highlights: { h: 38, s: 14, l: 2 },
        global: { h: 0, s: 0, l: 0 },
        blending: 50,
      },
      effects: { ...DEFAULT_GRADE.effects, grain: 14, vignette: 12, halation: 38, glow: 20 },
    },
  },
  {
    // "Tucker" — bright, airy tropical look reverse-engineered from a graded
    // Pipeline frame. Measured target: lifted "faded" blacks (black point ~0.16),
    // bright but unclipped highlights, clean neutral foam, a soft pale sky, and
    // vivid teal/turquoise water (low red, high green/blue). So vs. the old take
    // we LOWER contrast, LIFT blacks/shadows, ease the dehaze, and keep the water
    // pop in the aqua band while keeping the sky natural. Neutral profile avoids
    // the default S-curve so the look stays high-key. Authored as a display look
    // that stacks on top of a camera-log de-log (RED Log3G10) via applyLook.
    id: 'tucker',
    name: 'Tucker',
    kind: 'any',
    // Reverse-engineered from Tucker's graded frame of A009_C056_06015P.RDC, then
    // corrected to preserve the barrel's natural texture. Earlier takes added a
    // radial *lift/glow* in the pocket — but Tucker's real barrel is dark,
    // contrasty and TEXTURED (the faint wind-streak lines stay visible), not a
    // creamy glow. So we dropped the local mask and went texture-forward:
    // restore contrast, ease the black-lift, and push clarity/texture so the
    // micro-detail (wind lines, surface chop) reads through. Still pinned to the
    // clip's real auto-exposure (-0.7 EV) the way the app applies it; neutral
    // profile keeps it off the default S-curve. Authored as a display look that
    // stacks on a camera-log de-log (RED Log3G10) via applyLook.
    grade: {
      ...clone(DEFAULT_GRADE),
      profile: 'neutral',
      // Filmic highlight shoulder: the breaking lip / foam sits far above 1.0 after
      // the Log3G10 de-log, so with no rolloff it clipped to flat white (lost all
      // spray texture). A 'high' rolloff compresses those highlights back under 1.0
      // so the foam keeps its wispy feathering — the single biggest step toward
      // Tucker's controlled, luminous whitewater. exposure stays 0 here; the app
      // stacks the clip's auto-exposure (~-0.7 EV) on top.
      display: { ...DEFAULT_GRADE.display, rolloff: 'high' },
      wb: { temp: -5, tint: 9 },
      // exposure +0.5 keeps mids airy; whites/highlights pulled deep negative to
      // tame the (now rolled-off) foam down to a textured ~175-180 instead of a
      // blown 210+.
      basic: { ...DEFAULT_GRADE.basic, exposure: 0.5, contrast: 2, highlights: -40, shadows: -12, whites: -45, blacks: 10 },
      // Detail recipe carried over from the v9 reference-match work (#4 halo-free
      // micro-contrast): the 7x7 CLARITY unsharp is what RINGS on the wave face /
      // foam edges, so keep it LOW; lean on the 3x3 TEXTURE pass (water streaks,
      // surface chop) which is essentially halo-free, plus a light dehaze for
      // interior pop. Earlier this preset had it backwards (clarity 20 > texture
      // 16), which is exactly what made the breaking lip read crunchy/digital.
      presence: { ...DEFAULT_GRADE.presence, texture: 44, clarity: 8, dehaze: 4, vibrance: 44, saturation: 24, skinProtect: 55 },
      hsl: (() => {
        const h = Array.from({ length: HSL_BANDS }, () => ({ h: 0, s: 0, l: 0 }));
        h[3] = { h: 0, s: 18, l: 0 };     // green → push toward clean teal (kills murky green water)
        h[4] = { h: -22, s: 52, l: 12 };  // aqua → vivid turquoise (the lit wave face)
        h[5] = { h: 0, s: -34, l: 22 };   // blue → desaturate + lift so the sky/deep barrel stay soft & airy
        return h;
      })(),
      colorGrading: {
        shadows: { h: 192, s: 15, l: 10 },   // teal in the barrel, but less lift so it keeps depth + texture
        midtones: { h: 188, s: 3, l: 0 },    // faint teal through the mids
        highlights: { h: 45, s: 12, l: 0 },  // clean, faintly warm spray/sun (Tucker's foam is slightly warm)
        global: { h: 0, s: 0, l: 0 },
        blending: 50,
      },
      // Modest 5x5 sharpen for crispness + a whisper of grain to break the clean
      // digital sheen RED leaves on flat water/sky (also from the v9 work — the
      // single cheapest step away from the "plastic" look).
      effects: { ...DEFAULT_GRADE.effects, sharpness: 18, grain: 6 },
    },
  },
];

// The sensible default look for RED footage/stills: decode Log3G10 + RED Wide
// Gamut to a natural Rec.709 baseline (IPP2-style) so flat/monotone RED material
// looks normal before any manual grading. Applied automatically to RED frame
// grabs and seeded into the RED clip/photo editors; always editable & clearable.
export const RED_DEFAULT_GRADE = clone(
  (PRESETS.find((p) => p.id === 'red-ipp2') || { grade: DEFAULT_GRADE }).grade
);
